import React, { useEffect, useState } from "react";
import { ArrowLeftRight, ShieldCheck, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "../ui/Toast";

// Admin Transfers oversight tab (mounted in AdminPerformancePage). Approve has
// TWO side effects — StaffProfile move (dept/manager/title) AND rerouting the
// old manager's pending approval tasks to the new manager — so approve is a
// CONFIRMED action naming BOTH. Approve/reject only on pending (backend 409s
// otherwise). Reject requires a reason (backend 422s without). Status vocab:
// pending | completed | rejected (terminal success is "completed", NOT approved).

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  pending:   "text-amber-400 bg-amber-500/10 border-amber-500/25",
  completed: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  rejected:  "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { pending: "Pending", completed: "Completed", rejected: "Rejected" };
const TYPE_LABEL = { department: "Department", reporting_line: "Reporting Line", location: "Location", role: "Role" };
const FILTERS = ["ALL", "pending", "completed", "rejected"];

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const statusBadge = (s) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || STATUS_FALLBACK}`}>{STATUS_LABEL[s] || s || "—"}</span>
);

// Explicit consequence statement naming BOTH side effects for the approve confirm.
const consequence = (t) => {
  const name = t.employee_name || "this employee";
  const toMgr = t.to_manager_name || "the new manager";
  const from = t.from_manager_name || "the previous manager";
  const bits = [];
  if (t.to_department)   bits.push(`to ${t.to_department}`);
  if (t.to_manager_name) bits.push(`under ${t.to_manager_name}`);
  if (t.to_title)        bits.push(`as ${t.to_title}`);
  const move = bits.length ? `moves ${name} ${bits.join(" ")}` : `finalises this ${TYPE_LABEL[t.transfer_type] || t.transfer_type} for ${name}`;
  return `Approving ${move}, AND reassigns ${from}'s pending approval tasks to ${toMgr}. This writes their HR record.`;
};

export default function TransferTab() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [filter, setFilter]       = useState("ALL");
  const [panel, setPanel]         = useState(null);   // { id, kind: 'approve'|'reject' }
  const [notes, setNotes]         = useState("");
  const [reason, setReason]       = useState("");
  const [busy, setBusy]           = useState(false);

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, "Content-Type": "application/json" };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/performance/transfers`, { headers: authH });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTransfers(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); setTransfers([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openPanel = (id, kind) => {
    if (panel && panel.id === id && panel.kind === kind) { setPanel(null); return; }
    setNotes(""); setReason(""); setPanel({ id, kind });
  };

  const doApprove = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/transfers/${id}/approve`, {
        method: "PATCH", headers: jsonH, body: JSON.stringify({ notes: notes || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setTransfers(ts => ts.map(t => (t.id === id ? { ...t, ...updated } : t)));
      setPanel(null);
      toast.success("Approved — employee moved & approvals rerouted");
    } catch { toast.error("Failed to approve"); }
    finally { setBusy(false); }
  };

  const doReject = async (id) => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/transfers/${id}/reject`, {
        method: "PATCH", headers: jsonH, body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setTransfers(ts => ts.map(t => (t.id === id ? { ...t, ...updated } : t)));
      setPanel(null);
      toast.success("Rejected");
    } catch { toast.error("Failed to reject"); }
    finally { setBusy(false); }
  };

  const ordered = [...transfers]
    .filter(t => filter === "ALL" || t.status === filter)
    .sort((a, b) => (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1));
  const pendingCount = transfers.filter(t => t.status === "pending").length;

  return (
    <div className="space-y-5">
      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === f
                ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
            }`}>
            {f === "ALL" ? "All" : STATUS_LABEL[f]}
            {f === "pending" && pendingCount > 0 && <span className="ml-1 text-amber-400">({pendingCount})</span>}
          </button>
        ))}
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <ArrowLeftRight size={14} className="text-[#00ff90]" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Transfer Requests</span>
          {pendingCount > 0 && <span className="text-xs text-amber-400/70">· {pendingCount} awaiting decision</span>}
        </div>

        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>)
          ) : ordered.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No transfers{filter !== "ALL" ? ` with status "${STATUS_LABEL[filter]}"` : ""}.</p>
          ) : ordered.map(t => (
            <div key={t.id}>
              <div className="flex items-start justify-between gap-3 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-sm text-white/80 font-medium">{t.employee_name || t.employee_id}</p>
                    <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">{TYPE_LABEL[t.transfer_type] || t.transfer_type}</span>
                    {statusBadge(t.status)}
                  </div>
                  <p className="text-xs text-white/40">
                    Dept: {val(t.from_department)} → {val(t.to_department)} · Mgr: {val(t.from_manager_name)} → {val(t.to_manager_name)} · Title: {val(t.from_title)} → {val(t.to_title)}
                  </p>
                  <p className="text-xs text-white/25 mt-0.5">
                    Initiated by {t.initiated_by_name || t.initiated_by} · Effective {fmtDate(t.effective_date)}
                  </p>
                  {t.reason && <p className="text-xs text-white/40 mt-1">{t.reason}</p>}
                  {(t.status === "completed" || t.status === "rejected") && t.approval_notes && (
                    <p className="text-xs text-white/50 mt-0.5">
                      <span className="text-white/30">{t.status === "rejected" ? "Reason: " : "HR note: "}</span>{t.approval_notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  {t.status === "pending" && (
                    <>
                      <button onClick={() => openPanel(t.id, "approve")}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 transition-colors">
                        <ShieldCheck size={12} /> Approve
                      </button>
                      <button onClick={() => openPanel(t.id, "reject")}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-500/25 bg-red-500/5 text-red-300 hover:bg-red-500/15 transition-colors">
                        <XCircle size={12} /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>

              {panel && panel.id === t.id && (
                <div className="mx-5 mb-4 rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/3 p-4 space-y-3">
                  {panel.kind === "approve" && (
                    <>
                      <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
                        <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-200/80">{consequence(t)}</p>
                      </div>
                      <div>
                        <label className={labelCls}>HR Notes (optional)</label>
                        <input className={inputCls} value={notes} onChange={ev => setNotes(ev.target.value)} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPanel(null)} className="text-xs px-4 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                        <button onClick={() => doApprove(t.id)} disabled={busy}
                          className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                          Confirm Approve
                        </button>
                      </div>
                    </>
                  )}
                  {panel.kind === "reject" && (
                    <>
                      <div>
                        <label className={labelCls}>Reason <span className="text-amber-400/60">* required</span></label>
                        <textarea className={inputCls} rows={2} value={reason} onChange={ev => setReason(ev.target.value)}
                          placeholder="Why is this being rejected?" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPanel(null)} className="text-xs px-4 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                        <button onClick={() => doReject(t.id)} disabled={busy || !reason.trim()}
                          className="text-xs px-4 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 disabled:opacity-40 transition-colors">
                          Confirm Reject
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
